// Dashboard store using Zustand for KATA Dashboard

import { create } from 'zustand';
import { 
  DashboardService, 
  DashboardStats, 
  WorkshopStatusCounts, 
  SavingsMetrics, 
  DepartmentStats 
} from '../services/dashboard';

interface DashboardState {
  // Data
  stats: DashboardStats | null;
  statusCounts: WorkshopStatusCounts | null;
  savingsMetrics: SavingsMetrics | null;
  departmentStats: DepartmentStats[];
  
  // Filters
  deadlineFrom?: string;
  deadlineTo?: string;
  alvId?: string;
  
  // UI State
  isLoading: boolean;
  error: string | null;
  lastUpdated: Date | null;
}

interface DashboardActions {
  // Data fetching
  fetchDashboardData: () => Promise<void>;
  fetchStatusCounts: () => Promise<void>;
  fetchSavingsMetrics: () => Promise<void>;
  fetchDepartmentStats: () => Promise<void>;
  refreshAll: () => Promise<void>;
  
  // Filters
  setDeadlineFilter: (startDate?: string, endDate?: string) => void;
  setAlvIdFilter: (alvId?: string) => void;
  
  // UI state
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearError: () => void;
}

type DashboardStore = DashboardState & DashboardActions;

const STORAGE_KEY_FROM = 'kata_deadline_from';
const STORAGE_KEY_TO = 'kata_deadline_to';
const EVENT_NAME = 'kata_date_filter_change';
const STORAGE_KEY_ALV_ID = 'kata_plant_filter';
const PLANT_EVENT_NAME = 'kata_plant_filter_change';

export const useDashboardStore = create<DashboardStore>((set, get) => {
  // Listen for custom events to sync across stores
  if (typeof window !== 'undefined') {
    window.addEventListener(EVENT_NAME, (e: any) => {
      const { startDate, endDate } = e.detail;
      const { deadlineFrom, deadlineTo } = get();
      
      // Only update if values are different to avoid infinite loops
      if (startDate !== deadlineFrom || endDate !== deadlineTo) {
        set({ deadlineFrom: startDate, deadlineTo: endDate });
        // Optional: trigger refresh here if needed, or let the user manually refresh
        get().refreshAll();
      }
    });
    window.addEventListener(PLANT_EVENT_NAME, (e: any) => {
      const { alvId } = e.detail;
      const currentAlvId = get().alvId;
      
      if (alvId !== currentAlvId) {
        set({ alvId });
        get().refreshAll();
      }
    });

  }

  return {
  // Initial state
  stats: null,
  statusCounts: null,
  savingsMetrics: null,
  departmentStats: [],
  deadlineFrom: localStorage.getItem(STORAGE_KEY_FROM) || undefined,
  deadlineTo: localStorage.getItem(STORAGE_KEY_TO) || undefined,
  alvId: localStorage.getItem(STORAGE_KEY_ALV_ID) || undefined,
  isLoading: false,
  error: null,
  lastUpdated: null,

  // Set loading state
  setLoading: (isLoading: boolean) => {
    set({ isLoading });
  },

  // Set filters
  setDeadlineFilter: (startDate?: string, endDate?: string) => {
    if (startDate) {
      localStorage.setItem(STORAGE_KEY_FROM, startDate);
    } else {
      localStorage.removeItem(STORAGE_KEY_FROM);
    }

    if (endDate) {
      localStorage.setItem(STORAGE_KEY_TO, endDate);
    } else {
      localStorage.removeItem(STORAGE_KEY_TO);
    }
    
    // Set state BEFORE dispatching event so our own listener's guard check skips the duplicate refresh
    set({ deadlineFrom: startDate, deadlineTo: endDate });
    
    // Dispatch custom event to notify other stores
    const event = new CustomEvent(EVENT_NAME, { 
      detail: { startDate, endDate } 
    });
    window.dispatchEvent(event);

    // Trigger refresh when filters change
    get().refreshAll();
  },


  setAlvIdFilter: (alvId?: string) => {
    if (alvId) {
      localStorage.setItem(STORAGE_KEY_ALV_ID, alvId);
    } else {
      localStorage.removeItem(STORAGE_KEY_ALV_ID);
    }
    
    // Set state BEFORE dispatching event so our own listener's guard check skips the duplicate refresh
    set({ alvId });
    
    // Dispatch event to notify other stores (workshop, kata coaching)
    const event = new CustomEvent(PLANT_EVENT_NAME, { 
      detail: { alvId } 
    });
    window.dispatchEvent(event);

    get().refreshAll();
  },

  // Set error message
  setError: (error: string | null) => {
    set({ error });
  },

  // Clear error message
  clearError: () => {
    set({ error: null });
  },

  // Fetch comprehensive dashboard statistics
  fetchDashboardData: async () => {
    const { setLoading, setError, clearError, deadlineFrom, deadlineTo } = get();
    
    try {
      setLoading(true);
      clearError();
      
      const filters = {
        deadline_from: deadlineFrom,
        deadline_to: deadlineTo,
        alv_id: get().alvId
      };
      
      const stats = await DashboardService.getDashboardStats(filters);
      
      set({ 
        stats,
        lastUpdated: new Date()
      });
      
    } catch (error: any) {
      setError(error.message || 'Failed to fetch dashboard data');
    } finally {
      setLoading(false);
    }
  },

  // Fetch workshop status counts
  fetchStatusCounts: async () => {
    const { setError, clearError, deadlineFrom, deadlineTo } = get();
    
    try {
      clearError();
      
      const filters = {
        deadline_from: deadlineFrom,
        deadline_to: deadlineTo,
        alv_id: get().alvId
      };
      
      const statusCounts = await DashboardService.getWorkshopStatusCounts(filters);
      set({ statusCounts });
      
    } catch (error: any) {
      setError(error.message || 'Failed to fetch status counts');
    }
  },

  // Fetch savings metrics
  fetchSavingsMetrics: async () => {
    const { setError, clearError, deadlineFrom, deadlineTo } = get();
    
    try {
      clearError();
      
      const filters = {
        deadline_from: deadlineFrom,
        deadline_to: deadlineTo,
        alv_id: get().alvId
      };
      
      const savingsMetrics = await DashboardService.getSavingsMetrics(filters);
      set({ savingsMetrics });
      
    } catch (error: any) {
      setError(error.message || 'Failed to fetch savings metrics');
    }
  },

  // Fetch department statistics
  fetchDepartmentStats: async () => {
    const { setError, clearError, deadlineFrom, deadlineTo } = get();
    
    try {
      clearError();
      
      const filters = {
        deadline_from: deadlineFrom,
        deadline_to: deadlineTo,
        alv_id: get().alvId
      };
      
      const departmentStats = await DashboardService.getDepartmentStats(filters);
      set({ departmentStats });
      
    } catch (error: any) {
      setError(error.message || 'Failed to fetch department stats');
    }
  },

  // Refresh all dashboard data with a single API call
  refreshAll: async () => {
    const { setLoading, setError, clearError, deadlineFrom, deadlineTo, alvId } = get();
    
    try {
      setLoading(true);
      clearError();
      
      const filters = {
        deadline_from: deadlineFrom,
        deadline_to: deadlineTo,
        alv_id: alvId
      };
      
      // Single API call - derive all data from one response
      const stats = await DashboardService.getDashboardStats(filters);
      
      // Derive status counts
      const statusCounts: WorkshopStatusCounts = {
        active: stats.overview.ongoing_workshops,
        late: stats.timeline.overdue_workshops,
        completed: stats.overview.completed_workshops,
        total: stats.overview.total_workshops
      };
      
      // Derive savings metrics
      const savingsMetrics: SavingsMetrics = {
        estimated_savings: stats.financial.total_estimated_savings,
        released_savings: stats.financial.total_released_savings,
        realization_rate: stats.financial.realization_rate,
        formatted_estimated: DashboardService.formatCurrency(stats.financial.total_estimated_savings),
        formatted_released: DashboardService.formatCurrency(stats.financial.total_released_savings)
      };
      
      // Derive department stats
      const departmentColors = [
        '#10B981', '#3B82F6', '#F59E0B', '#8B5CF6', '#EF4444', '#06B6D4',
        '#F97316', '#EC4899', '#84CC16', '#6366F1', '#14B8A6', '#F43F5E'
      ];
      const departmentStats: DepartmentStats[] = stats.departments.map((dept, index) => ({
        id: dept.id,
        name: dept.name,
        code: dept.code,
        workshop_count: dept.workshop_count,
        completed: dept.completed,
        late: dept.late || 0,
        completion_rate: dept.workshop_count > 0 
          ? Math.round((dept.completed / dept.workshop_count) * 100) 
          : 0,
        estimated_savings: dept.estimated_savings,
        released_savings: dept.released_savings,
        color: departmentColors[index % departmentColors.length]
      }));
      
      set({ 
        stats,
        statusCounts,
        savingsMetrics,
        departmentStats,
        lastUpdated: new Date()
      });
      
    } catch (error: any) {
      setError(error.message || 'Failed to fetch dashboard data');
    } finally {
      setLoading(false);
    }
  },
}});

// Utility hooks for specific dashboard data
export const useDashboardStats = () => {
  const { stats, isLoading, error } = useDashboardStore();
  return { stats, isLoading, error };
};

export const useStatusCounts = () => {
  const { statusCounts, isLoading, error } = useDashboardStore();
  return { statusCounts, isLoading, error };
};

export const useSavingsMetrics = () => {
  const { savingsMetrics, isLoading, error } = useDashboardStore();
  return { savingsMetrics, isLoading, error };
};

export const useDepartmentStats = () => {
  const { departmentStats, isLoading, error } = useDashboardStore();
  return { departmentStats, isLoading, error };
};
